import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';

const ResourcesTab = ({ formId }) => {
  const [currentPath, setCurrentPath] = useState([]); // Array of folder names representing path
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [selectedFolderForThumbnail, setSelectedFolderForThumbnail] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);

  // Determine if we're at root level or in a subfolder
  const isRootLevel = currentPath.length === 0;
  const thumbnailHeight = isRootLevel ? 'h-[200px]' : 'h-[150px]';

  useEffect(() => {
    loadResources();
  }, [formId, currentPath]);

  const getCurrentFolderPath = () => {
    return currentPath.join('/');
  };

  const loadResources = async () => {
    try {
      setLoading(true);
      const folderPath = getCurrentFolderPath();
      const response = await axios.get(buildApiUrl(`/api/resources/list/${formId}${folderPath ? `?path=${encodeURIComponent(folderPath)}` : ''}`));
      if (response.data.success) {
        setFolders(response.data.folders || []);
        setFiles(response.data.files || []);
      }
    } catch (error) {
      console.error('Failed to load resources:', error);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const folderPath = getCurrentFolderPath();
      const response = await axios.post(buildApiUrl('/api/resources/create-folder'), {
        form_id: formId,
        folder_name: newFolderName.trim(),
        parent_path: folderPath
      });

      if (response.data.success) {
        setNewFolderName('');
        setShowCreateFolder(false);
        await loadResources();
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert('Failed to create folder');
    }
  };

  const openFolder = (folderName) => {
    setCurrentPath([...currentPath, folderName]);
  };

  const navigateUp = () => {
    if (currentPath.length > 0) {
      setCurrentPath(currentPath.slice(0, -1));
    }
  };

  const navigateToPath = (index) => {
    setCurrentPath(currentPath.slice(0, index + 1));
  };

  const handleFileSelect = (e) => {
    setUploadFiles(Array.from(e.target.files));
  };

  const uploadFilesToCurrentFolder = async () => {
    if (uploadFiles.length === 0) return;

    try {
      setUploading(true);
      const folderPath = getCurrentFolderPath();
      const formData = new FormData();
      formData.append('form_id', formId);
      formData.append('folder_path', folderPath);
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(buildApiUrl('/api/resources/upload'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setUploadFiles([]);
        await loadResources();
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (filename) => {
    if (!confirm(`Delete "${filename}"?`)) return;

    try {
      const folderPath = getCurrentFolderPath();
      const response = await axios.delete(buildApiUrl('/api/resources/delete-file'), {
        data: {
          form_id: formId,
          folder_path: folderPath,
          filename
        }
      });

      if (response.data.success) {
        await loadResources();
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file');
    }
  };

  const deleteFolder = async (folderName) => {
    if (!confirm(`Delete folder "${folderName}" and all its contents?`)) return;

    try {
      const parentPath = getCurrentFolderPath();
      const fullPath = parentPath ? `${parentPath}/${folderName}` : folderName;
      const response = await axios.delete(buildApiUrl('/api/resources/delete-folder'), {
        data: {
          form_id: formId,
          folder_path: fullPath
        }
      });

      if (response.data.success) {
        await loadResources();
      }
    } catch (error) {
      console.error('Failed to delete folder:', error);
      alert('Failed to delete folder');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    // Return appropriate emoji/icon for file type
    const iconMap = {
      // Documents
      'pdf': '📕',
      'doc': '📘',
      'docx': '📘',
      'txt': '📄',
      'rtf': '📄',
      
      // Spreadsheets
      'xls': '📗',
      'xlsx': '📗',
      'csv': '📊',
      
      // Presentations
      'ppt': '📙',
      'pptx': '📙',
      
      // Images
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'bmp': '🖼️',
      'svg': '🖼️',
      'webp': '🖼️',
      
      // Videos
      'mp4': '🎬',
      'mov': '🎬',
      'avi': '🎬',
      'mkv': '🎬',
      'wmv': '🎬',
      'flv': '🎬',
      'webm': '🎬',
      
      // Audio
      'mp3': '🎵',
      'wav': '🎵',
      'flac': '🎵',
      'aac': '🎵',
      'm4a': '🎵',
      
      // Archives
      'zip': '📦',
      'rar': '📦',
      '7z': '📦',
      'tar': '📦',
      'gz': '📦',
      
      // Code
      'js': '📜',
      'py': '📜',
      'java': '📜',
      'cpp': '📜',
      'html': '📜',
      'css': '📜',
      'json': '📜',
      'xml': '📜'
    };
    
    return iconMap[ext] || '📎';
  };

  const isImageFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext);
  };

  const isVideoFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'].includes(ext);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const openThumbnailModal = (folderName) => {
    setSelectedFolderForThumbnail(folderName);
    setShowThumbnailModal(true);
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setThumbnailFile(file);
    } else {
      alert('Please select an image file');
    }
  };

  const uploadThumbnail = async () => {
    if (!thumbnailFile || !selectedFolderForThumbnail) return;

    try {
      const folderPath = getCurrentFolderPath();
      const fullPath = folderPath ? `${folderPath}/${selectedFolderForThumbnail}` : selectedFolderForThumbnail;
      
      const formData = new FormData();
      formData.append('form_id', formId);
      formData.append('folder_path', fullPath);
      formData.append('thumbnail', thumbnailFile);

      const response = await axios.post(buildApiUrl('/api/resources/upload-thumbnail'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setShowThumbnailModal(false);
        setThumbnailFile(null);
        setSelectedFolderForThumbnail(null);
        await loadResources();
      }
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      alert('Failed to upload thumbnail');
    }
  };

  const getFolderThumbnail = (folderName) => {
    const folderPath = getCurrentFolderPath();
    const fullPath = folderPath ? `${folderPath}/${folderName}` : folderName;
    return buildApiUrl(`/api/resources/thumbnail/${formId}?path=${encodeURIComponent(fullPath)}`);
  };

  return (
    <div className="space-y-6">
      {/* Header with Breadcrumb Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">📚 Class Resources</h2>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mt-2 text-sm">
            <button
              onClick={() => setCurrentPath([])}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              📁 Root
            </button>
            {currentPath.map((folder, index) => (
              <React.Fragment key={index}>
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => navigateToPath(index)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {folder}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {currentPath.length > 0 && (
            <button
              onClick={navigateUp}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
          )}
          <button
            onClick={() => setShowCreateFolder(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Folder
          </button>
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              onKeyPress={(e) => e.key === 'Enter' && createFolder()}
            />
            <div className="flex gap-3">
              <button
                onClick={createFolder}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Thumbnail Modal */}
      {showThumbnailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Folder Thumbnail</h3>
            <p className="text-sm text-gray-600 mb-4">Upload a photo for "{selectedFolderForThumbnail}"</p>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailSelect}
              className="w-full mb-4"
            />
            {thumbnailFile && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">✓ {thumbnailFile.name}</p>
                <img 
                  src={URL.createObjectURL(thumbnailFile)} 
                  alt="Preview" 
                  className="mt-2 max-h-32 rounded-lg"
                />
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={uploadThumbnail}
                disabled={!thumbnailFile}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Upload
              </button>
              <button
                onClick={() => {
                  setShowThumbnailModal(false);
                  setThumbnailFile(null);
                  setSelectedFolderForThumbnail(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Files Section */}
      {currentPath.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📤 Upload Files</h3>
          <div className="flex items-center gap-4">
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="flex-1"
            />
            {uploadFiles.length > 0 && (
              <span className="text-sm text-gray-600">{uploadFiles.length} file(s)</span>
            )}
            <button
              onClick={uploadFilesToCurrentFolder}
              disabled={uploading || uploadFiles.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && folders.length === 0 && files.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <div className="text-6xl mb-4">📁</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {currentPath.length > 0 ? 'Folder is Empty' : 'No Folders Yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {currentPath.length > 0 
              ? 'Upload files or create subfolders to organize your resources'
              : 'Create your first folder to organize class resources'}
          </p>
          <button
            onClick={() => setShowCreateFolder(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Folder
          </button>
        </div>
      )}

      {/* Folders and Files Grid */}
      {!loading && (folders.length > 0 || files.length > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {/* Folders */}
          {folders.map((folder) => (
            <div
              key={folder.name}
              onDoubleClick={() => openFolder(folder.name)}
              className="cursor-pointer group"
            >
              {/* Thumbnail or Folder Icon */}
              <div className={`w-full ${thumbnailHeight} bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center relative rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all`}>
                <img 
                  src={getFolderThumbnail(folder.name)}
                  alt={folder.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
                  <span className="text-6xl">📁</span>
                </div>
                {/* Add Thumbnail button on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openThumbnailModal(folder.name);
                  }}
                  className="absolute bottom-2 left-2 p-2 bg-blue-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity text-xs flex items-center gap-1"
                  title="Add thumbnail"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Photo
                </button>
                {/* Delete button on hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFolder(folder.name);
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete folder"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              {/* Folder Info - Outside thumbnail */}
              <div className="mt-2 text-center">
                <p className="text-base font-medium text-gray-900 truncate" title={folder.name}>
                  {folder.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {folder.item_count || 0} items
                </p>
              </div>
            </div>
          ))}

          {/* Files */}
          {files.map((file, idx) => (
            <div
              key={idx}
              className="group relative"
            >
              <a
                href={buildApiUrl(file.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className={`w-full ${thumbnailHeight} bg-gray-50 flex items-center justify-center relative overflow-hidden rounded-lg border border-gray-200 hover:shadow-lg hover:border-blue-300 transition-all`}>
                  {isImageFile(file.name) ? (
                    <img 
                      src={buildApiUrl(file.url)}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : isVideoFile(file.name) ? (
                    <video 
                      src={buildApiUrl(file.url)}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                  ) : null}
                  <div className={`${isImageFile(file.name) || isVideoFile(file.name) ? 'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent' : ''} flex items-center justify-center w-full h-full`} style={{ display: isImageFile(file.name) || isVideoFile(file.name) ? 'none' : 'flex' }}>
                    <div className="text-5xl">{getFileIcon(file.name)}</div>
                  </div>
                </div>
              </a>
              {/* File Info - Outside thumbnail */}
              <div className="mt-2 text-center">
                <p className="text-base font-medium text-gray-900 truncate" title={file.name}>
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{formatFileSize(file.size)}</p>
              </div>
              {/* Delete button on hover */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteFile(file.name);
                }}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete file"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourcesTab;
